import {
  Box,
  Center,
  Flex,
  HStack,
  Heading,
  HeadingProps,
  Icon,
  Separator,
  Text,
  TextProps,
} from '@chakra-ui/react';
import { RecordAuditInfo } from '@monetize/types/common';
import { MigratedQuoteIcon } from '@monetize/ui/icons';
import { FC, Fragment } from 'react';
import { MdArrowDropDown } from 'react-icons/md';
import { useAuth } from '../../services/auth0';
import { MFlex, MHStack, MIcon } from './chakra';
import { EntityJsonViewerIcon } from './EntityJsonViewerIcon';
import { MBackButton } from './MBackButton';
import { MEntityStatusBadge } from './MEntityStatusBadge';
import { MParentLink } from './MParentLink';
import { MStatusIDBox } from './MStatusIDBox';
import MText from './MText';
import { RecordAuditInfoPopover } from './RecordAuditInfoPopover';

interface MPageHeaderLeftSectionProp {
  title?: string | React.ReactNode;
  id?: string;
  size?: HeadingProps['size'];
  tag?: React.ReactNode; // TODO: Rename tag prop to something generic
  tagStyle?: TextProps;
  subTitle?: React.ReactNode;
  hasBackButton?: boolean;
  copyUrl?: string | boolean;
  recordAuditInfo?: RecordAuditInfo;
  parentLink?: string;
  parentLinkTitle?: string;
  /** Alternate react component for the id */
  customIdCopyBox?: React.ReactNode;
  /** Added after parent link and id with dividers between */
  extraSubtitleParts?: React.ReactNode[];
  status?: string;
  migrated?: boolean;
  firstRowPlaceholder?: React.ReactNode;
  onToggle?: () => void;
  extraBadges?: React.ReactNode[];
  entityJson?: Record<string, unknown>;
}

export const MPageHeaderLeftSection: FC<MPageHeaderLeftSectionProp> = ({
  hasBackButton,
  size = 'lg',
  title,
  tag,
  tagStyle,
  parentLink,
  parentLinkTitle,
  id,
  copyUrl,
  recordAuditInfo,
  customIdCopyBox,
  extraSubtitleParts,
  subTitle,
  status,
  migrated,
  firstRowPlaceholder,
  onToggle,
  extraBadges = [],
  entityJson,
}) => {
  const { tenantId, userId } = useAuth();

  return (
    <Flex>
      {hasBackButton && (
        <MBackButton
          containerProps={{
            mt: 0.5,
          }}
        />
      )}

      {/* Negative margin + matching padding gives 4px of breathing room
          around the entire left section so focus halos on links and copy
          buttons aren't clipped by ancestor containers. The hasBackButton
          ml is preserved by adding 1.5 to the negative-margin baseline. */}
      <Box ml={hasBackButton ? 0.5 : -1} mr="-1" my="-1" p="1">
        <Flex align="center" mb="0.5">
          {onToggle && (
            <Flex
              align="center"
              _hover={{ background: 'tGray.support', cursor: 'pointer' }}
              onClick={onToggle}
            >
              <Heading
                size={size}
                color="tPurple.base"
                lineClamp={2}
                // Negative margin + matching padding gives focus halos on link
                // titles room to render in all 4 directions. `lineClamp` forces
                // overflow:hidden, which would otherwise clip the link's 3px
                // focus shadow.
                m="-1"
                p="1"
              >
                {title}
              </Heading>
              <Icon h={6} w={6} asChild>
                <MdArrowDropDown />
              </Icon>
            </Flex>
          )}
          {!onToggle && (
            <Heading
              size={size}
              color="tPurple.base"
              lineClamp={2}
              // Negative margin + matching padding gives focus halos on link
              // titles room to render in all 4 directions. `lineClamp` forces
              // overflow:hidden, which would otherwise clip the link's 3px
              // focus shadow.
              m="-1"
              p="1"
            >
              {title}
            </Heading>
          )}
          {status && <MEntityStatusBadge status={status} />}
          {migrated && (
            <MFlex
              bgColor="tPurple.linkWater"
              pr={1}
              borderRadius={2}
              height="18px"
              ml="0.5rem"
              alignItems="center"
            >
              <MIcon as={MigratedQuoteIcon} boxSize="18px" />
              <MText m={0} color="tBlue.light">
                Migrated
              </MText>
            </MFlex>
          )}
          {!!tag && (
            <Text
              as="span"
              fontSize="sm"
              ml={2}
              color="tGray.darkGrayPurple"
              lineHeight="1.125rem"
              {...tagStyle}
            >
              {tag}
            </Text>
          )}
          {extraBadges.length > 0 && <MHStack>{extraBadges}</MHStack>}
        </Flex>
        {firstRowPlaceholder}
        <HStack flexWrap="wrap" ml={0} gap={2} alignItems="center" mb={1}>
          <MParentLink
            parentLink={parentLink}
            parentLinkTitle={parentLinkTitle}
          />
          {!!parentLink && !!parentLinkTitle && !!id && (
            <Center m={0} w={0} height={3}>
              <Separator
                m={0}
                orientation="vertical"
                borderColor="tGray.darkGrayPurple"
              />
            </Center>
          )}
          <MStatusIDBox
            id={id}
            copyUrl={copyUrl}
            customIdCopyBox={customIdCopyBox}
          />
          {recordAuditInfo && tenantId && userId && (
            <RecordAuditInfoPopover
              recordAuditInfo={recordAuditInfo}
              tenantId={tenantId}
              userId={userId}
            />
          )}
          <EntityJsonViewerIcon json={entityJson} />

          {extraSubtitleParts?.map((part, i) => (
            <Fragment key={i}>
              {/* Determine if divider should be rendered based on a prior item existing */}
              {(i !== 0 || !!parentLink || !!id) && (
                <Center m={0} w={0} height={3}>
                  <Separator
                    m={0}
                    orientation="vertical"
                    borderColor="tGray.darkGrayPurple"
                  />
                </Center>
              )}
              {part}
            </Fragment>
          ))}
        </HStack>

        {subTitle}
      </Box>
    </Flex>
  );
};
